
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/auth';
import { useToast } from '@/hooks/use-toast';
import { SalaryVerificationSettings } from '@/types/salaryVerification';

export function useSalaryVerificationSettings() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [settings, setSettings] = useState<SalaryVerificationSettings | null>(null);

  useEffect(() => {
    const fetchSettings = async () => {
      const { data, error } = await supabase
        .from('salary_verification_settings')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error fetching verification settings:', error);
        return;
      }

      setSettings(data);
    };

    fetchSettings();
  }, []);

  const updateSettings = async (newSettings: Partial<SalaryVerificationSettings>) => {
    if (!settings) {
      // Create new settings if none exist
      const { data, error } = await supabase
        .from('salary_verification_settings')
        .insert({
          is_enabled: newSettings.is_enabled || false,
          verification_message: newSettings.verification_message || 'กรุณายืนยันข้อมูลส่วนตัวของคุณก่อนเข้าดูข้อมูลเงินเดือน',
          created_by: user?.id
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating settings:', error);
        toast({
          title: 'เกิดข้อผิดพลาด',
          description: 'ไม่สามารถสร้างการตั้งค่าได้',
          variant: 'destructive'
        });
        return false;
      }

      setSettings(data);
      toast({
        title: 'สร้างสำเร็จ',
        description: 'การตั้งค่าระบบยืนยันได้รับการสร้างแล้ว'
      });
      return true;
    }

    // Try to update the existing settings
    const { data, error } = await supabase
      .from('salary_verification_settings')
      .update({
        ...newSettings,
        updated_at: new Date().toISOString()
      })
      .eq('id', settings.id)
      .select()
      .maybeSingle();

    if (error) {
      console.error('Error updating settings:', error);
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถอัพเดทการตั้งค่าได้',
        variant: 'destructive'
      });
      return false;
    }

    // If no data returned, it means the record was deleted, create a new one
    if (!data) {
      const { data: newData, error: createError } = await supabase
        .from('salary_verification_settings')
        .insert({
          is_enabled: newSettings.is_enabled || false,
          verification_message: newSettings.verification_message || 'กรุณายืนยันข้อมูลส่วนตัวของคุณก่อนเข้าดูข้อมูลเงินเดือน',
          created_by: user?.id
        })
        .select()
        .single();

      if (createError) {
        console.error('Error creating new settings:', createError);
        toast({
          title: 'เกิดข้อผิดพลาด',
          description: 'ไม่สามารถสร้างการตั้งค่าใหม่ได้',
          variant: 'destructive'
        });
        return false;
      }

      setSettings(newData);
      toast({
        title: 'สร้างสำเร็จ',
        description: 'การตั้งค่าระบบยืนยันได้รับการสร้างใหม่แล้ว'
      });
      return true;
    }

    setSettings(data);
    toast({
      title: 'อัพเดทสำเร็จ',
      description: 'การตั้งค่าระบบยืนยันได้รับการอัพเดทแล้ว'
    });
    return true;
  };

  return {
    settings,
    updateSettings
  };
}
